import {
  type ColumnDef,
  type ColumnPinningState,
  columnPinningFeature,
  createPaginatedRowModel,
  createSortedRowModel,
  type OnChangeFn,
  type PaginationState,
  type RowData,
  type RowSelectionState,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  type SortingState,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  type TableOptions,
  tableFeatures,
  useTable,
} from "@tanstack/react-table";

type DataTableColumnMeta = {
  alignment?: "left" | "center" | "right";
  cellClassName?: string;
  headerClassName?: string;
  tone?: "default" | "muted";
};

export const dataTableFeatures = tableFeatures({
  columnPinningFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text,
  },
  columnMeta: {} as DataTableColumnMeta,
});

export type DataTableColumnDef<TData extends RowData> = ColumnDef<typeof dataTableFeatures, TData>;
export type DataTableInstance<TData extends RowData> = ReturnType<typeof useDataTable<TData>>;
export type DataTableDensity = "default" | "compact";

export type UseDataTableOptions<TData extends RowData> = Pick<
  TableOptions<typeof dataTableFeatures, TData>,
  "enableMultiRowSelection" | "enableRowSelection" | "getRowId" | "manualSorting" | "onSortingChange"
> & {
  /** 使用逻辑方向，保留当前项目的列固定 API。 */
  columnPinning?: ColumnPinningState;
  columns: ReadonlyArray<DataTableColumnDef<TData>>;
  data: ReadonlyArray<TData>;
  enableSorting?: boolean;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  pagination?: PaginationState;
  rowSelection?: RowSelectionState;
  sorting?: SortingState;
};

/** HRMS 的状态/视图分层，适配当前项目的 TanStack Table v9。 */
export function useDataTable<TData extends RowData>({
  columnPinning,
  columns,
  data,
  enableMultiRowSelection,
  enableRowSelection,
  enableSorting = true,
  getRowId,
  manualSorting,
  onRowSelectionChange,
  onSortingChange,
  pagination,
  rowSelection,
  sorting,
}: UseDataTableOptions<TData>) {
  return useTable({
    columns,
    data,
    enableMultiRowSelection,
    enableRowSelection,
    enableSorting,
    features: dataTableFeatures,
    getRowId,
    // 服务端分页已经返回当前页；只有显式传入 pagination 时才在客户端切页。
    manualPagination: !pagination,
    autoResetPageIndex: false,
    manualSorting,
    maxMultiSortColCount: 3,
    onRowSelectionChange,
    onSortingChange,
    state: {
      ...(columnPinning ? { columnPinning } : {}),
      ...(pagination ? { pagination } : {}),
      ...(rowSelection ? { rowSelection } : {}),
      ...(sorting ? { sorting } : {}),
    },
  });
}
